import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * App-shell state ONLY (D6): theme, sidebar, command palette.
 * Server state lives exclusively in TanStack Query — putting it here is a
 * review-blocking violation.
 */
export type Theme = 'light' | 'dark' | 'system'
/** Register/control density (Fiix audit §8.2). `auto` = compact on a fine pointer, comfortable on touch. */
export type Density = 'auto' | 'comfortable' | 'compact'

interface AppShellState {
  theme: Theme
  density: Density
  sidebarOpen: boolean
  commandPaletteOpen: boolean
  setTheme: (theme: Theme) => void
  setDensity: (density: Density) => void
  toggleSidebar: () => void
  setCommandPaletteOpen: (open: boolean) => void
}

export const useAppShell = create<AppShellState>()(
  persist(
    (set) => ({
      theme: 'system',
      density: 'auto',
      sidebarOpen: true,
      commandPaletteOpen: false,
      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
    }),
    {
      name: 'floorfile-shell',
      partialize: (s) => ({ theme: s.theme, density: s.density, sidebarOpen: s.sidebarOpen }),
    },
  ),
)

/**
 * Resolve 'auto' density against the device: a fine pointer (mouse, trackpad)
 * gets compact rows; a coarse one (touch) keeps the 44 px comfortable rows a
 * gloved hand needs. The same device switching pointer (a convertible with a
 * detached keyboard) re-resolves through the media query listener.
 */
export function resolveDensity(density: Density): 'comfortable' | 'compact' {
  if (density !== 'auto') return density
  if (typeof window === 'undefined') return 'comfortable'
  return window.matchMedia('(pointer: fine)').matches ? 'compact' : 'comfortable'
}

/** Resolve 'system' against the OS preference. */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}
